import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRBAC } from '../hooks/usePermissions';
import type { Permission, UserRole } from '../types/Auth';

interface ProtectedRouteProps {
    children: React.ReactNode;
    permissions?: Permission[];
    roles?: UserRole[];
    requireAll?: boolean;
    redirectTo?: string;
    fallback?: React.ReactNode;
}

export function ProtectedRoute({ 
    children, 
    permissions = [],
    roles = [],
    requireAll = false,
    redirectTo = "/login",
    fallback
}: ProtectedRouteProps) {
    const { isAuthenticated } = useAuth();
    const { canAccess } = useRBAC();

    // First check authentication
    if (!isAuthenticated) {
        return <Navigate to={redirectTo} replace />;
    }

    // If no specific permissions or roles required, just check authentication
    if (permissions.length === 0 && roles.length === 0) {
        return <>{children}</>;
    }

    // Check RBAC permissions
    const hasAccess = canAccess({
        permissions,
        roles,
        requireAll,
    });

    if (!hasAccess) {
        if (fallback) {
            return <>{fallback}</>;
        }

        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
}

// Convenience components for common protection scenarios
interface RequirePermissionRouteProps {
    children: React.ReactNode;
    permission: Permission;
    redirectTo?: string;
    fallback?: React.ReactNode;
}

export function RequirePermissionRoute({ 
    children, 
    permission, 
    redirectTo = "/login",
    fallback 
}: RequirePermissionRouteProps) {
    return (
        <ProtectedRoute 
            permissions={[permission]} 
            redirectTo={redirectTo}
            fallback={fallback}
        >
            {children}
        </ProtectedRoute>
    );
}

interface RequireRoleRouteProps {
    children: React.ReactNode;
    role: UserRole;
    redirectTo?: string;
    fallback?: React.ReactNode;
}

export function RequireRoleRoute({ 
    children, 
    role, 
    redirectTo = "/login",
    fallback 
}: RequireRoleRouteProps) {
    return (
        <ProtectedRoute 
            roles={[role]} 
            redirectTo={redirectTo}
            fallback={fallback}
        >
            {children}
        </ProtectedRoute>
    );
}

interface AdminRouteProps {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

export function AdminRoute({ children, fallback }: AdminRouteProps) {
    return (
        <RequireRoleRoute role="admin" fallback={fallback}>
            {children}
        </RequireRoleRoute>
    );
} 